import type { PropType } from "vue";
import { defineComponent } from "vue";
import type { showTotalFn, TableFieldParam, TableItem } from "@/types";
import { isBoolean, isFunction, isString, orderBy, toFloat, get } from "@/utils";
import { Decimal } from "decimal.js-light";
import Fuse from "fuse.js";
import TableCell from "@/components/TableCell.vue";
import { BButton, BFormGroup, BFormSelect } from "bootstrap-vue-next";
import type { TableField, TableItem as TI } from "bootstrap-vue-next/dist/src/types";
import type { BTable } from "bootstrap-vue-next";
import type { TableFieldFormatter } from "bootstrap-vue-next/dist/src/types";
import { useSettingsStore } from "@/stores/settings";


type T = Record<keyof never, unknown>;

type DataType = {
  selected?: TableItem<T, unknown>[];
  sortDesc: boolean;
  sortBy: string;
  currentPage: number;
  perPage: undefined | number;
  downloadDataLoading: boolean;
  searchEngine: null | Fuse<T>;
  paginationOptions: { text: string; value: string | null }[];
  internalFilter: null | string;
};


function formatItemsCsv(items: TI[], fields: TableField[], formatted: boolean = true): string[][] {
  const result = [];
  // header
  const header: string[] = [];
  for (const field of fields) {
    if (isString(field)) {
      header.push(field);
    } else {
      if (field.label) header.push(field.label);
      else header.push(field.key);
    }
  }
  result.push(header);
  // rows
  for (const item of items) {
    const row: string[] = [];
    for (const field of fields) {
      if (isString(field)) {
        row.push(get(item, field, ""))
      } else {
        const key: string = field.key
        if (formatted) {
          const v = formatItem(item, key, field.formatter)
          row.push(`${v}`);
        } else {
          row.push(get(item, key, ""));
        }
      }
    }
    result.push(row);
  }
  return result
}

function formatItem(
  item: TI,
  fieldKey: string,
  formatter?: TableFieldFormatter<typeof item>
): string {
  const val = get(item, fieldKey, "")
  if (formatter && typeof formatter === 'function') {
    return formatter(val, fieldKey, item)
  }
  return `${val}`
}


type FilterFn = (item: T, filter: string | null) => boolean;

export default defineComponent({
  components: { BFormGroup, BButton, BFormSelect, TableCell },
  props: {
    filter: {
      type: String as PropType<string | FilterFn>,
      required: false,
    },
    articoli: {
      type: Array as PropType<T[]>,
      required: true,
      default: () => [],
    },
    rowKey: {
      type: String as PropType<keyof T>,
      required: true,
    },

    fields: {
      type: Array as PropType<TableField[]>,
      required: true,
      default: () => [],
    },
    busy: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    selectable: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    selectMode: {
      type: String as PropType<"multi" | "single" | "range">,
      required: false,
      default: () => "multi",
    },
    defaultSortDesc: {
      type: Boolean,
      required: false,
    },
    defaultSortBy: {
      type: String,
      required: false,
    },
    usePagination: {
      type: Boolean,
      required: false,
      default: () => true,
    },
    footerNote: {
      type: String,
      required: false,
    },
    showFooter: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    showDownloadButton: {
      type: Boolean,
      required: false,
      default: () => true,
    },
    showFilter: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    autoHeight: {
      type: Boolean,
      required: false,
      default: () => false,
    }
  },
  data: (): DataType => ({
    selected: [],
    sortDesc: false,
    sortBy: "",
    currentPage: 1,
    perPage: undefined,
    downloadDataLoading: false,
    searchEngine: null,
    internalFilter: null,
    paginationOptions: [
      { text: '50', value: '50' },
      { text: '100', value: '100' },
      { text: '200', value: '200' },
      { text: 'Tutti', value: null }
    ],
  }),
  computed: {
    totalRows(): number {
      return this.filtered?.length ?? 0
    },
    savedPerPage(): number {
      const val = localStorage.getItem("tabellaarticoli.perpage");
      const defaultValue = toFloat(this.paginationOptions[this.paginationOptions.length - 2].value, 50)
      if (val === null || val === "null") {
        // l'opzione "tutti" è volutamente ignorata come valore di default
        // per evitare blocchi del browser in caso di tabelle molto grandi
        return defaultValue
      }
      return toFloat(val, defaultValue);
    },
    totalPages: function (): number {
      if (!this.perPage) return 1;
      return Math.ceil(this.totalRows / this.perPage);
    },
    table(): typeof BTable {
      return this.$refs.table as typeof BTable;
    },
    items(): T[] {
      // se la pagina corrente non esiste viene impostata sull'ultima pagina
      if (this.usePagination && this.currentPage > this.totalPages && this.totalPages >= 1) {
        console.log("[TabellaArticoli.ts] change page from ", this.currentPage, "to", this.totalPages);
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.currentPage = this.totalPages;
      }
      if (!this.usePagination || !this.perPage) {
        return this.sortItems(this.filtered);
      } else {
        return this.paginate(this.filtered);
      }
    },
    filtered(): T[] {
      if (this.showFilter) {
        return this._filterItems(this.articoli, this.internalFilter);
      } else {
        return this._filterItems(this.articoli, this.filter ?? null);
      }
    },
    showPagination(): boolean {
      if (!this.perPage) return false;
      try {
        return this.totalRows > this.perPage;
      } catch (err) {
        return false;
      }
    }
  },
  mounted() {
    this.sortDesc = this.defaultSortDesc ?? this.sortDesc;
    this.sortBy = this.defaultSortBy ?? this.sortBy;
    console.log(`[TabellaArticoli.ts] sort ${this.sortBy} ${this.sortDesc}`);
    this.perPage = this.savedPerPage;
    this.setupSearchEngine(this.articoli)
  },
  // watch articoli props
  watch: {
    articoli: {
      handler: function (val: T[]) {
        console.log("[TabellaArticoli.ts] articoli changed", val);
        this.currentPage = 1;
        this.setupSearchEngine(val)

      },
      deep: true,
    },
  },

  methods: {
    paginate(array: T[]): T[] {
      const pagSize = parseInt(`${this.perPage ?? "20"}`);
      const sorted = this.sortItems(array);
      let i: number;
      // numero di pagine massime
      const maxPages = Math.ceil(sorted.length / pagSize);
      // se la pagina corrente non esiste
      if (this.currentPage > maxPages) {
        i = maxPages - 1; // viene restituita l'ultima pagina
      } else {
        i = this.currentPage - 1;
      }
      return sorted.slice(i * pagSize, this.currentPage * pagSize);
    },
    sortItems(items: T[]): T[] {
      return orderBy(items, [this.sortBy], this.sortDesc ? ["desc"] : ["asc"]);
    },
    getClass(data: TableFieldParam<T, never>): string | null {
      console.log("[TabellaArticoli.ts] GETCLASS", data);
      return "table-secondary";
    },
    setPerPage($event: Event): void {
      const value = ($event.target as HTMLInputElement).value
      localStorage.setItem("tabellaarticoli.perpage", `${value}`);
      this.perPage = parseInt(value as string);
    },
    validateAll(): boolean {
      let i = 0;
      for (const row of this.articoli || []) {
        if (!this.table) continue;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        for (const [fieldName, field] of Object.entries((this.table as any).computedFields)) {
          if (!this.fieldIsValid(row, fieldName, field as never, i)) {
            return false;
          }
        }
        i++;
      }
      return true;
    },
    /**
     * propaga i cambiamenti fatti nella cella all'oggetto
     */
    onCellChange(
      {
        name,
        row,
        value,
        item,
      }: {
        name: keyof T;
        row: string;
        value: never;
        item: TableItem<T, unknown>;
      }): void {
      if (name == undefined) return; // TODO: capire perchè arrivano undefined
      if (row == undefined) return;
      console.log("[TabellaArticoli.ts] onCellChange", {
        name,
        row,
        value,
        item,
      });
      if (!this.rowKey) return;
      if (!item) return;
      const articolo = this.articoli.find((articolo) => this.rowKey && articolo[this.rowKey] === row);
      if (articolo) {
        articolo[name] = value;
        item.item = articolo;
      }
      if (item.field.editable?.onChange) {
        item.field.editable.onChange(item, row);
      }
    },
    setupSearchEngine(val: T[]) {
      const searchEngineOpt = {
        includeScore: true,
        keys: [] as string[],
        isCaseSensitive: false,
        threshold: 0.2,
        useExtendedSearch: true,
        ignoreLocation: true,
      };
      if (val && val.length > 0) {
        searchEngineOpt.keys = Object.keys(val[0]);
      } else {
        searchEngineOpt.keys = [];
      }
      this.searchEngine = new Fuse(val, searchEngineOpt);
    },
    showTotal(fields: TableFieldParam<T, never>[]): boolean {
      for (const field of fields) {
        if (field.showTotal) return true;
      }
      return false;
    },
    getFields(data: TI): TableFieldParam<T, never>[] {
      return data.fields as TableFieldParam<T, never>[];
    },
    getTotal(field: TableFieldParam<T, never>): string {
      function getK<R>(item: T, key: keyof T): R {
        return item[key] as R;
      }

      let total: string | number | Decimal | null = null;
      if (isBoolean(field.showTotal)) {
        const key = field.key as keyof T;
        if (this.articoli.length === 0) {
          total = new Decimal("0");
        } else {
          total = this.articoli.reduce<Decimal>((cumm, b) => {
            return cumm.add(getK<number>(b, key) ?? 0);
          }, new Decimal("0"));
        }
        if (field.fieldFormatter) {
          return `${field.fieldFormatter(total, field.key, undefined)}`;
        } else {
          return `${total}`;
        }
      } else if (field.showTotal) {
        total = (field.showTotal as showTotalFn)();
        return `${total}`;
      }
      if (field.fieldFormatter) return `${field.fieldFormatter(total, field.key, undefined)}`;
      else return `${total}`;
    },
    sv<T>(val: T | undefined, def: T): T {
      return val ?? def;
    },
    showTotalCssClass<R>(field: TableFieldParam<T, R>): string[] {
      if (field.showTotalCssClass) {
        return field.showTotalCssClass();
      } else {
        return ["number", "no-wrap"];
      }
    },
    fieldIsValid<R>(row: T, fieldName: keyof T, field: TableFieldParam<T, R>, index: number): boolean {
      if (field.validation) {
        const item: TableItem<T, R> = {
          item: row,
          index: index,
          field: field,
          key: 0,
          items: [],
          value: row[fieldName] as R,
          detailsShowing: false,
          rowSelected: false,
        };
        const result = field.validation(item);
        return result === true;
      }
      return true;
    },
    getLabel(data: { [key: string]: unknown }): string {
      return get(data, "label", "") as string
    },
    getRow(data: { [key: string]: unknown }): string {
      const item = get(data, "item", {}) as T;
      return `${item[this.rowKey]}`
    },
    async downloadData() {
      try {
        this.downloadDataLoading = true
        const settingsStore = useSettingsStore();
        const response = await settingsStore.wsClient.ExportTable({
          data: formatItemsCsv(this.articoli, this.fields, false),
          header: true
        })
        console.log("exported", response)
        if (response?.Success) {
          const bytes = new Uint8Array(response.Data);
          const blob = new Blob([bytes], { type: "application/octet-stream" });
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = "data.xlsx";
          link.click();
          link.remove();
        }
      } finally {
        this.downloadDataLoading = false
      }
    },
    _filterItems(items: T[], searchValue: string | null | FilterFn): T[] {
      if (!searchValue) return items;
      if (!items || !items.length) return [];
      if (isFunction(searchValue)) {
        const filterFn = searchValue as FilterFn;
        // TODO: change and test
        return items.filter((item) => filterFn(item, ""));
      }
      if (isString(searchValue) && this.searchEngine) {
        const result = this.searchEngine.search(searchValue as string);
        return result.map((e) => e.item);
      }
      return items;
    },
    _onRowSelected(items: TableItem<T, unknown>[]): void {
      this.$emit("row-selected", items);
      console.log("_onRowSelected", items, this.table);
      // FUCK MICROSOFT'S DEVS !
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-expect-error
      this.selected = items;
    }
  },
});
